import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'

import RentalPlan, { correctRentalPlan } from '../helpers/rental_plan'
import SalePlan from '../helpers/sale_plan'
import CurrencyUSD, { dollarStringToNumber } from '../misc/currency_formats'

import styles from '../../assets/stylesheets/pages/cart/schedule.module.scss'

import { Alert, Button } from 'lensrentals-components'
import DatePicker from './date_picker'

const Schedule = props => {
  const [dateCorrectionMessage, setDateCorrectionMessage] = useState(null)

  useEffect(() => {
    // grab message out of props and cache it here so it can persist client side when the server returns a new cartPlan
    if (props.dateCorrectionMessage) {
      setDateCorrectionMessage(props.dateCorrectionMessage)
    }
  }, [props.dateCorrectionMessage])

  const {
    className,
    updateCartPlan,
    cartPlan
  } = props

  const {
    proposedEarlierArrival,
    earliestShipDate
  } = cartPlan
  const isRental = cartPlan.type === 'rental'

  let earlierShippingPriceDifference
  let isProposedPlanValid
  if (proposedEarlierArrival && !!cartPlan.withProposedNewDates) {
    const proposedArrival = proposedEarlierArrival.arrivalDate
    const proposedPlan = cartPlan.withProposedNewDates({
      newArrival: proposedArrival,
      newReturn: proposedEarlierArrival.returnDate
    })
    // if the plan requires no changes, it won't have a message
    // also we don't want to make a suggestion that will force a change of ship type
    const correctedProposedPlan = correctRentalPlan(proposedPlan)
    isProposedPlanValid = !correctedProposedPlan.message && earliestShipDate !== proposedArrival
    earlierShippingPriceDifference = dollarStringToNumber(proposedEarlierArrival.subtotal) - dollarStringToNumber(cartPlan.subtotal)
  }

  const UseEarlierSuggestedDate = () => {
    const changeToEarlierDate = () => updateCartPlan(cartPlan.withNewDates({
      newArrival: proposedEarlierArrival.arrivalDate,
      newReturn: proposedEarlierArrival.returnDate
    }))

    return (
      <div className={cx('alert', styles.ship_earlier_suggestion, styles.schedule__alert)}>
        <p>
          Get your rental a business day early for only <CurrencyUSD amount={earlierShippingPriceDifference} /> more.
        </p>
        <Button
          className={styles['earlier-shipping']}
          onClick={changeToEarlierDate}
          color='confirm'
        >
          Move my arrival date
        </Button>
      </div>
    )
  }

  // the nextjs checkout page handles scheduling for the sale cart
  if (!isRental) return null

  return (
    <section
      id='schedule'
      className={cx(
        className,
        styles.schedule
      )}
    >
      <div className={styles.schedule__header}>
        <span className={`${styles['schedule__header-amount']} price`}>
          <CurrencyUSD amount={cartPlan.subtotal} />
        </span>
        {isRental &&
          <>
            <span className={styles['schedule__header-separator']} />
            <span className={`${styles['schedule__header-text']} rental-period`}>
              <span className={cx('duration', styles['schedule__header-duration'])}>
                {cartPlan.duration} Day
              </span>
              <span className={styles['schedule__header-type']}>
                Rental
              </span>
            </span>
          </>}
      </div>
      {!!dateCorrectionMessage &&
        (
          <Alert
            className={cx('error', styles.schedule__alert)}
            show={!!dateCorrectionMessage}
            onClose={() => setDateCorrectionMessage(null)}
            selfDestruct
            color='danger'
          >
            <span>{dateCorrectionMessage}</span>
          </Alert>
        )}

      {isRental &&
        (
          <DatePicker
            updateCartPlan={updateCartPlan}
            cartPlan={cartPlan}
          />
        )}

      {(isRental && earlierShippingPriceDifference && isProposedPlanValid)
        ? <UseEarlierSuggestedDate />
        : null}
    </section>
  )
}

Schedule.propTypes = {
  className: PropTypes.string,
  updateCartPlan: PropTypes.func.isRequired,
  cartPlan: PropTypes.oneOfType([
    PropTypes.instanceOf(RentalPlan),
    PropTypes.instanceOf(SalePlan)
  ]).isRequired,
  dateCorrectionMessage: PropTypes.string
}

export default Schedule
